<script setup lang="ts">
// Sales-offer wizard (Lior's spec): 1) choose a project overview or a specific unit, 2) personalise
// (saved branding, developer toggle, optional client name and note), 3) preview, then download or
// share. Once presentation settings are saved, later offers skip step 2.
import type { PropertyDetailed } from '~/types/properties';
import { useBrandKitQuery } from '~/api/queries/marketing';
import {
  useGenerateSalesOffer,
  usePreviewSalesOffer,
  useSaveOfferSettings,
  type SalesOfferCopy,
  type SalesOfferInput,
} from '~/api/mutations/deals/useGenerateSalesOffer';

type Step = 'choose' | 'personalise' | 'preview';
type Mode = 'project' | 'unit';
type UnitOption = { id: string; title: string; meta: string; price: string };

const CLIENT_NAME_MAX = 120;
const MESSAGE_MAX = 600;
const SQFT_PER_M2 = 10.7639;
const UNAVAILABLE_STATUS_RE = /sold|reserv|book|hold/i;

const {
  open,
  item,
  propertyId,
  initialUnitId = null,
} = defineProps<{
  open: boolean;
  item: PropertyDetailed;
  propertyId: string;
  initialUnitId?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();
const { formatNumber } = useFormat();
const uiStore = useUIStore();
const authStore = useAuthStore();

const { data: brandKit } = useBrandKitQuery();
const previewOffer = usePreviewSalesOffer();
const createOffer = useGenerateSalesOffer();
const saveSettings = useSaveOfferSettings();

const step = ref<Step>('choose');
const mode = ref<Mode>('project');
const unitId = ref<string | null>(null);
const showDeveloper = ref(true);
const clientName = ref('');
const personalMessage = ref('');
const rememberSettings = ref(true);
const previewHtml = ref('');
const previewCopy = ref<SalesOfferCopy | null>(null);
// The stored offer for the current preview, so Download and Share don't create it twice.
const storedOfferUrl = ref<string | null>(null);

// Real, sellable units only: an offer quotes one exact unit and price.
const unitOptions = computed<UnitOption[]>(() =>
  ((item.apartments ?? []) as Array<Record<string, unknown>>)
    .filter((a) => {
      const price = Number((a.price as { min?: unknown } | undefined)?.min);
      return a.id != null && price > 0 && !UNAVAILABLE_STATUS_RE.test(String(a.status ?? ''));
    })
    .map((a) => {
      const rooms = (a.rooms_count as { en?: string } | undefined)?.en ?? '';
      const m2 = Number((a.area_min as { m2?: unknown } | undefined)?.m2);
      const area = m2 > 0 ? `${formatNumber(Math.round(m2 * SQFT_PER_M2))} sq.ft` : null;
      const floor = a.floor ? t('property.salesOfferWizard.floor', { floor: String(a.floor) }) : null;
      const price = Number((a.price as { min?: unknown }).min);
      return {
        id: String(a.id),
        title: a.number ? `${rooms} #${a.number}`.trim() : rooms,
        meta: [rooms && a.number ? null : rooms, area, floor].filter(Boolean).join(' · '),
        price: `${(a.price as { currency?: string }).currency || 'AED'} ${formatNumber(price)}`,
      };
    }),
);

const hasSavedSettings = computed(() => !!brandKit.value?.offerSettingsSavedAt);
const agentName = computed(
  () =>
    brandKit.value?.displayName ||
    `${authStore.user?.firstName ?? ''} ${authStore.user?.lastName ?? ''}`.trim() ||
    authStore.user?.email ||
    '',
);
const agentContacts = computed(() =>
  [
    brandKit.value?.whatsapp || brandKit.value?.phone || authStore.user?.phone,
    brandKit.value?.email || authStore.user?.email,
  ]
    .filter(Boolean)
    .join(' · '),
);

const canContinue = computed(() => mode.value === 'project' || !!unitId.value);
const isWorking = computed(() => previewOffer.isPending.value || createOffer.isPending.value);

const reset = () => {
  const startsFromUnit = !!initialUnitId && unitOptions.value.some((u) => u.id === initialUnitId);
  step.value = 'choose';
  mode.value = startsFromUnit ? 'unit' : 'project';
  unitId.value = startsFromUnit ? initialUnitId : (unitOptions.value[0]?.id ?? null);
  showDeveloper.value = brandKit.value?.offerShowDeveloper ?? true;
  clientName.value = '';
  personalMessage.value = '';
  rememberSettings.value = true;
  previewHtml.value = '';
  previewCopy.value = null;
  storedOfferUrl.value = null;
};

watch(
  () => open,
  (isOpen) => {
    if (isOpen) reset();
  },
  { immediate: true },
);
watch(
  () => brandKit.value?.offerShowDeveloper,
  (saved) => {
    if (step.value === 'choose' && typeof saved === 'boolean') showDeveloper.value = saved;
  },
);

const offerInput = (copy: SalesOfferCopy | null): SalesOfferInput => ({
  propertyId,
  unitId: mode.value === 'unit' ? unitId.value : null,
  clientName: clientName.value.trim() || undefined,
  personalMessage: personalMessage.value.trim() || undefined,
  showDeveloper: showDeveloper.value,
  copy,
});

const loadPreview = async () => {
  step.value = 'preview';
  previewHtml.value = '';
  storedOfferUrl.value = null;
  try {
    const result = await previewOffer.mutateAsync(offerInput(null));
    previewHtml.value = result.html;
    previewCopy.value = result.copy;
  } catch {
    step.value = hasSavedSettings.value ? 'choose' : 'personalise';
  }
};

const onChooseContinue = () => {
  if (!canContinue.value) return;
  if (hasSavedSettings.value) {
    loadPreview();
    return;
  }
  step.value = 'personalise';
};

const onPersonaliseContinue = async () => {
  if (rememberSettings.value) {
    try {
      await saveSettings.mutateAsync(showDeveloper.value);
    } catch {
      // Saving the default failed; the offer itself can still go ahead.
    }
  }
  loadPreview();
};

const ensureStoredOffer = async (): Promise<string | null> => {
  if (storedOfferUrl.value) return storedOfferUrl.value;
  const res = await createOffer.mutateAsync(offerInput(previewCopy.value));
  storedOfferUrl.value = res?.data?.offerUrl ?? null;
  return storedOfferUrl.value;
};

const onDownload = async () => {
  try {
    const url = await ensureStoredOffer();
    if (url) window.open(url, '_blank', 'noopener');
    uiStore.showSnackBanner('success', t('property.salesOfferWizard.ready'));
  } catch {
    // useErrorHandler already showed the reason.
  }
};

const copyOfferLink = async (url: string) => {
  await navigator.clipboard.writeText(url);
  uiStore.showSnackBanner('success', t('property.salesOfferWizard.linkCopied'));
};

const onShare = async () => {
  let url: string | null = null;
  try {
    url = await ensureStoredOffer();
    if (!url) return;
    const title = t('property.salesOfferWizard.shareTitle', { project: item.title?.en ?? '' });
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      await navigator.share({ title, url });
      return;
    }
    await copyOfferLink(url);
  } catch (error) {
    // Safari refuses the share sheet once the save has used up the tap; copy the link instead.
    // A dismissed sheet (AbortError) or a failed save (already reported) needs nothing more.
    if (url && error instanceof DOMException && error.name === 'NotAllowedError') {
      await copyOfferLink(url).catch(() => undefined);
    }
  }
};

const whatsappShareUrl = computed(() =>
  storedOfferUrl.value ? `https://wa.me/?text=${encodeURIComponent(storedOfferUrl.value)}` : null,
);

const onWhatsApp = () => {
  if (whatsappShareUrl.value) window.open(whatsappShareUrl.value, '_blank', 'noopener');
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black-100/40 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      :aria-label="t('property.salesOfferWizard.title')"
      @click.self="emit('close')"
    >
      <div
        class="my-auto w-full overflow-hidden rounded-2xl bg-white shadow-[0_24px_64px_-24px_rgba(13,15,21,0.45)]"
        :class="step === 'preview' ? 'max-w-[1040px]' : 'max-w-[560px]'"
      >
        <!-- Header -->
        <div class="flex items-center justify-between gap-3 border-b border-black-10 px-5 py-4">
          <div>
            <p class="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-black-60">
              {{ t('property.salesOfferWizard.title') }}
            </p>
            <p class="mt-0.5 text-[15px] font-semibold text-black-100">
              {{ t(`property.salesOfferWizard.steps.${step}`) }}
            </p>
          </div>
          <button
            type="button"
            class="rounded-lg px-2 py-1 text-[13px] text-black-60 hover:bg-black-05"
            @click="emit('close')"
          >
            {{ t('property.salesOfferWizard.close') }}
          </button>
        </div>

        <!-- Step 1 — Choose your offer -->
        <div
          v-if="step === 'choose'"
          class="flex flex-col gap-4 p-5"
        >
          <p class="text-[13px] font-semibold text-black-80">{{ t('property.salesOfferWizard.whatToSend') }}</p>
          <div class="grid gap-2">
            <label
              v-for="option in ['project', 'unit'] as const"
              :key="option"
              class="flex cursor-pointer items-start gap-3 rounded-xl border p-3"
              :class="mode === option ? 'border-black-100 bg-black-03' : 'border-black-10'"
            >
              <input
                v-model="mode"
                type="radio"
                :value="option"
                :disabled="option === 'unit' && !unitOptions.length"
                class="mt-1"
              />
              <span>
                <span class="block text-[14px] font-semibold text-black-100">
                  {{ t(`property.salesOfferWizard.modes.${option}.title`) }}
                </span>
                <span class="block text-[12px] text-black-60">
                  {{
                    option === 'unit' && !unitOptions.length
                      ? t('property.salesOfferWizard.noUnits')
                      : t(`property.salesOfferWizard.modes.${option}.description`)
                  }}
                </span>
              </span>
            </label>
          </div>

          <div
            v-if="mode === 'unit'"
            class="flex flex-col gap-2"
          >
            <p class="text-[13px] font-semibold text-black-80">{{ t('property.salesOfferWizard.whichUnit') }}</p>
            <div class="max-h-[260px] overflow-y-auto rounded-xl border border-black-10">
              <label
                v-for="unit in unitOptions"
                :key="unit.id"
                class="flex cursor-pointer items-center justify-between gap-3 border-b border-black-05 px-3 py-2.5 last:border-b-0"
                :class="unitId === unit.id ? 'bg-black-03' : ''"
              >
                <span class="flex items-center gap-3">
                  <input
                    v-model="unitId"
                    type="radio"
                    :value="unit.id"
                  />
                  <span>
                    <span class="block text-[13px] font-semibold text-black-100">{{ unit.title }}</span>
                    <span
                      v-if="unit.meta"
                      class="block text-[12px] text-black-60"
                      >{{ unit.meta }}</span
                    >
                  </span>
                </span>
                <span class="shrink-0 text-[13px] font-semibold text-black-100">{{ unit.price }}</span>
              </label>
            </div>
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <UIButton
              id="offer-wizard-cancel"
              variant="secondary"
              @click="emit('close')"
            >
              {{ t('property.salesOfferWizard.cancel') }}
            </UIButton>
            <UIButton
              id="offer-wizard-continue"
              :disabled="!canContinue"
              :loading="isWorking"
              @click="onChooseContinue"
            >
              {{
                hasSavedSettings ? t('property.salesOfferWizard.previewOffer') : t('property.salesOfferWizard.continue')
              }}
            </UIButton>
          </div>
        </div>

        <!-- Step 2 — Personalise -->
        <div
          v-else-if="step === 'personalise'"
          class="flex flex-col gap-4 p-5"
        >
          <div class="flex items-center justify-between gap-3 rounded-xl border border-black-10 p-3">
            <div class="flex min-w-0 items-center gap-3">
              <img
                v-if="brandKit?.logoUrl || brandKit?.profileImageUrl"
                :src="(brandKit?.logoUrl || brandKit?.profileImageUrl) ?? ''"
                alt=""
                class="size-10 shrink-0 rounded-lg object-contain"
              />
              <div class="min-w-0">
                <p class="truncate text-[14px] font-semibold text-black-100">{{ agentName }}</p>
                <p class="truncate text-[12px] text-black-60">{{ agentContacts }}</p>
              </div>
            </div>
            <NuxtLink
              to="/marketing?screen=brandkit"
              target="_blank"
              class="shrink-0 rounded-lg border border-black-10 px-3 py-1.5 text-[12px] font-semibold text-black-80 hover:bg-black-03"
            >
              {{ t('property.salesOfferWizard.editBranding') }}
            </NuxtLink>
          </div>

          <label class="flex items-start justify-between gap-4">
            <span>
              <span class="block text-[13px] font-semibold text-black-100">
                {{ t('property.salesOfferWizard.showDeveloper') }}
              </span>
              <span class="block text-[12px] text-black-60">{{
                t('property.salesOfferWizard.showDeveloperHint')
              }}</span>
            </span>
            <input
              v-model="showDeveloper"
              type="checkbox"
              class="mt-1 size-5 shrink-0"
              data-testid="offer-show-developer"
            />
          </label>

          <label class="flex flex-col gap-1.5 text-[13px] font-semibold text-black-80">
            {{ t('property.salesOfferWizard.clientName') }}
            <input
              v-model="clientName"
              type="text"
              :maxlength="CLIENT_NAME_MAX"
              autocomplete="off"
              :placeholder="t('property.salesOfferWizard.clientNamePlaceholder')"
              class="h-11 rounded-lg border border-black-10 px-3 text-[14px] font-normal text-black-100"
              data-testid="offer-client-name"
            />
          </label>

          <label class="flex flex-col gap-1.5 text-[13px] font-semibold text-black-80">
            {{ t('property.salesOfferWizard.message') }}
            <textarea
              v-model="personalMessage"
              rows="3"
              :maxlength="MESSAGE_MAX"
              :placeholder="t('property.salesOfferWizard.messagePlaceholder')"
              class="rounded-lg border border-black-10 px-3 py-2 text-[14px] font-normal text-black-100"
            />
          </label>

          <label class="flex items-center gap-2 text-[13px] text-black-80">
            <input
              v-model="rememberSettings"
              type="checkbox"
              class="size-4"
            />
            {{ t('property.salesOfferWizard.remember') }}
          </label>

          <div class="flex justify-between gap-2 pt-2">
            <UIButton
              id="offer-wizard-back"
              variant="secondary"
              @click="step = 'choose'"
            >
              {{ t('property.salesOfferWizard.back') }}
            </UIButton>
            <UIButton
              id="offer-wizard-preview"
              :loading="isWorking || saveSettings.isPending.value"
              @click="onPersonaliseContinue"
            >
              {{ t('property.salesOfferWizard.previewOffer') }}
            </UIButton>
          </div>
        </div>

        <!-- Step 3 — Preview and share -->
        <div
          v-else
          class="flex flex-col"
        >
          <div class="relative h-[70vh] bg-black-05">
            <div
              v-if="previewOffer.isPending.value || !previewHtml"
              class="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[13px] text-black-60"
            >
              <UIProgressSpinner />
              {{ t('property.salesOfferWizard.writing') }}
            </div>
            <iframe
              v-else
              :srcdoc="previewHtml"
              :title="t('property.salesOfferWizard.previewFrameTitle')"
              sandbox=""
              class="size-full border-0"
            />
          </div>
          <div class="flex flex-wrap items-center justify-between gap-2 border-t border-black-10 px-5 py-4">
            <div class="flex gap-2">
              <UIButton
                id="offer-wizard-edit"
                variant="secondary"
                :disabled="isWorking"
                @click="step = 'personalise'"
              >
                {{ t('property.salesOfferWizard.edit') }}
              </UIButton>
              <UIButton
                id="offer-wizard-change"
                variant="secondary"
                :disabled="isWorking"
                @click="step = 'choose'"
              >
                {{ t('property.salesOfferWizard.changeOffer') }}
              </UIButton>
            </div>
            <div class="flex flex-wrap gap-2">
              <UIButton
                v-if="whatsappShareUrl"
                id="offer-wizard-whatsapp"
                variant="secondary"
                @click="onWhatsApp"
              >
                {{ t('property.salesOfferWizard.whatsapp') }}
              </UIButton>
              <UIButton
                id="offer-wizard-share"
                variant="secondary"
                :disabled="!previewHtml"
                :loading="createOffer.isPending.value"
                @click="onShare"
              >
                {{ t('property.salesOfferWizard.share') }}
              </UIButton>
              <UIButton
                id="offer-wizard-download"
                :disabled="!previewHtml"
                :loading="createOffer.isPending.value"
                @click="onDownload"
              >
                {{ t('property.salesOfferWizard.download') }}
              </UIButton>
            </div>
          </div>
          <p
            v-if="mode === 'unit'"
            class="px-5 pb-4 text-[12px] text-black-60"
          >
            {{ t('property.salesOfferWizard.holdNote') }}
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>
